import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace, Workspace } from '../contexts/WorkspaceContext'
import styles from './WorkspacePickerPage.module.css'

// handoff §5 / V4 "Choose workspace": the app's entry point for an account
// with 2+ subscriptions, and what the sidebar brand block reopens. A screen,
// not a modal.

// V4's demo gives each workspace a tone from the palette; nothing stores one,
// so it is derived from the id and stays the same for a workspace every time.
const TONES = ['var(--dp-accent)', 'var(--dp-brand)', 'var(--dp-danger)', 'var(--dp-muted)']

function toneFor(id: string) {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return TONES[h % TONES.length]
}

export function initialsFor(name: string) {
  const words = name.split(/\s+/).filter((w) => /[A-Za-z0-9]/.test(w))
  return words
    .slice(0, 2)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, '')[0] || '')
    .join('')
    .toUpperCase()
}

function matches(w: Workspace, q: string) {
  return (w.name + ' ' + w.role).toLowerCase().includes(q)
}

export function WorkspacePickerPage() {
  const { user, signOut } = useAuth()
  const { subscriptions, selectWorkspace, staff } = useWorkspace()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')

  const q = query.trim().toLowerCase()
  const shown = subscriptions.filter((w) => !q || matches(w, q))
  const count = q ? `${shown.length} of ${subscriptions.length} match` : `${subscriptions.length} workspaces`
  // §5: the list is a fixed height set by the FULL list (capped), so the
  // centred card never shrinks and re-centres under the cursor while typing.
  // In rem, so it grows with the rows at a larger text size (h 1553).
  const listHeight = Math.min(430, Math.max(96, subscriptions.length * 88 - 10))

  const email = user?.email || ''
  const userInitials = (user?.displayName ? initialsFor(user.displayName) : email.slice(0, 2).toUpperCase()) || 'U'

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign-out failed:', err)
    }
  }

  const pick = (id: string) => {
    selectWorkspace(id)
    navigate('/dashboard', { replace: true })
  }

  return (
    <div className={styles.screen} data-screen-label="Choose workspace">
      <div className={styles.glowWarm} aria-hidden="true" />
      <div className={styles.glowSage} aria-hidden="true" />

      <div className={styles.brand}>
        <svg viewBox="0 0 149 151" width="24" className={styles.brandMark} aria-hidden="true">
          <path d="M74.49,2.91H3.63V117.08c17.9-1.59,44.14-9.49,62.73-28.46,4.33-4.29,7.18-7.62,10.22-13.01h-.73c-10.3,14.59-25.38,22.47-39.38,28.21-2.99-9.33-6.99-27.06,9.82-41.79,5.49-4.82,10.99-6.82,16.31-8.76,7.78-2.84,15.82-5.78,25.38-17.73l1.5-1.87,1.67,1.72c16.13,16.68,17.97,46.17,3.86,61.88-12.7,14.14-29.67,18.09-41.66,18.09-4.36,0-9.3-.98-11.77-1.86-12.26,5.94-24.93,10.14-37.94,11.86v19.28H74.49c39.14,0,70.87-31.73,70.87-70.87S113.63,2.91,74.49,2.91Z" />
        </svg>
        <span className={styles.brandName}>CONTROL</span>
      </div>

      {email && (
        <div className={styles.userChip}>
          <span className={styles.userEmail}>{email}</span>
          <span className={styles.userMark}>{userInitials}</span>
          <button type="button" className={styles.signOut} onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      )}

      <div className={styles.card}>
        <h1 className={styles.title}>Which workspace?</h1>
        <p className={styles.lede}>Pick one to continue. You can switch any time from the sidebar.</p>

        {/* Dataphyll staff: their own area, outside every client workspace, first in the list. */}
        {staff && (
          <Link to="/god-mode" className={`${styles.row} ${styles.godRow}`} title="Every client, creating one, products and licences">
            <span className={styles.mark} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="m12 2 2.9 6.6 7.1.6-5.4 4.7 1.6 7.1L12 17.3 5.8 21l1.6-7.1L2 9.2l7.1-.6z" />
              </svg>
            </span>
            <span className={styles.rowText}>
              <span className={styles.rowName}>God Mode</span>
            </span>
            <span className={styles.role}>Dataphyll staff</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" className={styles.chevron} aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </Link>
        )}

        <div className={styles.searchRow}>
          <label className={styles.search}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search workspaces or your role…"
              aria-label="Search workspaces"
              autoFocus
            />
          </label>
          <span className={styles.count}>{count}</span>
        </div>

        <div className={styles.list} style={{ height: `${listHeight / 16}rem` }} role="group" aria-label="Workspaces">
          {shown.map((w) => (
            <button key={w.id} type="button" className={styles.row} onClick={() => pick(w.id)}>
              <span className={styles.mark} style={{ background: toneFor(w.id) }} aria-hidden="true">
                {initialsFor(w.name)}
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowName} title={w.name}>{w.name}</span>
              </span>
              <span className={styles.role}>{w.role}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" className={styles.chevron} aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          ))}
          {shown.length === 0 && <div className={styles.empty}>No workspace matches that.</div>}
        </div>

        <p className={styles.footnote}>You can switch workspace any time from the sidebar.</p>
      </div>
    </div>
  )
}
